/**
 * SourcesPanel — UI completa para gestionar Sources.
 *
 * Reutilizable: el caller decide si es project_sources (pasa
 * `mode='project'` + projectId + phaseOptions) o tenant_sources
 * (`mode='tenant'`).
 *
 * Encapsula:
 *  - Header con totales + botón "Subir".
 *  - Filtros (origin, archived).
 *  - Tabla de sources con descargar/archivar/borrar.
 *  - Empty state.
 *  - Dialog de upload.
 *  - Toasts de éxito/error.
 */

import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Archive, Download, Trash2, Upload } from 'lucide-react'
import i18n from 'i18next'

import { ApiError, isForbiddenTier } from '../../api/client'
import { retryUnlessForbiddenTier } from '../../api/queryRetry'
import {
  archiveProjectSource,
  archiveTenantSource,
  deleteProjectSource,
  deleteTenantSource,
  downloadProjectSource,
  downloadTenantSource,
  listProjectSources,
  listTenantSources,
  uploadProjectSource,
  uploadTenantSource,
  type ProjectSource,
  type SourceOrigin,
  type TenantSource,
  SOURCE_ORIGINS,
} from '../../api/sources'
import { triggerBrowserDownload } from '../../api/download'
import { useAccessToken } from '../../auth/useAccessToken'
import { isSupportedLocale, toIntlLocale } from '../../i18n/config'
import { pushToast } from '../../stores/toastStore'
import { UploadSourceDialog, type PhaseOption } from './UploadSourceDialog'

type SourcesMode = 'project' | 'tenant'

export interface SourcesPanelProps {
  mode: SourcesMode
  /** Required cuando mode='project'. */
  projectId?: string
  /** Lista de fases del proyecto (para el combo en upload + filtros).
   * Solo aplica en mode='project'. */
  phaseOptions?: PhaseOption[]
  /** Si el panel se abrió ya filtrado por una fase (ej. desde el
   * drill-in del Lifecycle), arrancamos con ese filtro aplicado. */
  defaultPhaseFilter?: string | undefined
  /** OT-0009 §1.2 #9 — decide "Subir"/"Archivar"/"Borrar". El caller lo
   * calcula: `canManageProjectSources` en modo proyecto, `isAdmin` en
   * modo tenant. "Descargar" no depende de esto — accesible a quien ya
   * puede ver la fuente. */
  canManage: boolean
  /** OT-0015 — qué mostrar en lugar del panel si la lectura responde
   * `403 FORBIDDEN_TIER` (el tier cambió con la sesión abierta). Con este
   * prop, ese 403 no se reintenta y los datos previos dejan de mostrarse.
   * Sin él, el panel se comporta como siempre. Lo pasa la base de
   * conocimiento del tenant, que es un recurso interno cerrado al
   * `external`; las fuentes de proyecto no lo necesitan. */
  forbiddenTierFallback?: ReactNode
}

type AnySource = ProjectSource | TenantSource

export function SourcesPanel({
  mode,
  projectId,
  phaseOptions,
  canManage,
  defaultPhaseFilter,
  forbiddenTierFallback,
}: SourcesPanelProps) {
  const { t } = useTranslation(['sources', 'errors'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const [originFilter, setOriginFilter] = useState<SourceOrigin | ''>('')
  const [phaseFilter, setPhaseFilter] = useState<string>(
    defaultPhaseFilter ?? '',
  )
  const [includeArchived, setIncludeArchived] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)

  // Query key alineada para que mutations puedan invalidar.
  const queryKey = useMemo(
    () =>
      mode === 'project'
        ? (['sources', 'project', projectId, 'list'] as const)
        : (['sources', 'tenant', 'list'] as const),
    [mode, projectId],
  )

  const sourcesQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<AnySource[]> => {
      if (!accessToken) throw new Error('no_access_token')
      if (mode === 'project') {
        if (!projectId) throw new Error('projectId_required')
        return listProjectSources(accessToken, projectId, {
          origin: originFilter || undefined,
          phase_id: phaseFilter || undefined,
          include_archived: includeArchived,
        })
      }
      return listTenantSources(accessToken, {
        origin: originFilter || undefined,
        include_archived: includeArchived,
      })
    },
    enabled: !!accessToken && (mode === 'tenant' || !!projectId),
    staleTime: 30_000,
    ...(forbiddenTierFallback !== undefined ? { retry: retryUnlessForbiddenTier } : {}),
  })

  // Refetch when filters change (TanStack: queryKey identidad ya
  // soporta esto vía closure; aquí sí lo hacemos manual con effect).
  // Para mantener simple, refetch via setQueryData no — confiamos en
  // que el panel re-llama queryFn cuando los filtros cambian.
  // Truco: incluir filtros en el queryKey arriba para invalidación
  // automática. Lo simplifico:
  const sources = useMemo(() => sourcesQuery.data ?? [], [sourcesQuery.data])
  // Filtrado client-side adicional (ya filtramos en backend, pero esto
  // captura el caso de cambio rápido de filtros sin refetch).
  const filteredSources = useMemo(() => {
    let rows = sources
    if (originFilter) {
      rows = rows.filter((r) => r.origin === originFilter)
    }
    if (mode === 'project' && phaseFilter) {
      rows = rows.filter(
        (r) => 'phase_id' in r && r.phase_id === phaseFilter,
      )
    }
    return rows
  }, [sources, originFilter, phaseFilter, mode])

  const totalSize = filteredSources.reduce(
    (acc, r) => acc + r.size_bytes,
    0,
  )

  // ─── Mutations ──────────────────────────────────────────────────────
  const uploadMutation = useMutation({
    mutationFn: async (input: {
      file: File
      origin: SourceOrigin
      phaseId: string | undefined
      tags: string[]
      description: string | undefined
    }) => {
      if (!accessToken) throw new Error('no_access_token')
      if (mode === 'project') {
        if (!projectId) throw new Error('projectId_required')
        return uploadProjectSource(accessToken, projectId, {
          file: input.file,
          origin: input.origin,
          phaseId: input.phaseId,
          tags: input.tags,
          description: input.description,
        })
      }
      return uploadTenantSource(accessToken, {
        file: input.file,
        origin: input.origin,
        tags: input.tags,
        description: input.description,
      })
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('toasts.uploaded'),
      })
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: (err) => {
      const code = err instanceof ApiError ? err.code : null
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', {
            defaultValue: t('toasts.upload_failed'),
          })
        : t('toasts.upload_failed')
      pushToast({ kind: 'critical', message, duration: 6000 })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      if (!accessToken) throw new Error('no_access_token')
      if (mode === 'project') {
        return archiveProjectSource(accessToken, sourceId)
      }
      return archiveTenantSource(accessToken, sourceId)
    },
    onSuccess: () => {
      pushToast({ kind: 'success', message: t('toasts.archived') })
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: () => {
      pushToast({
        kind: 'critical',
        message: t('toasts.archive_failed'),
        duration: 6000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      if (!accessToken) throw new Error('no_access_token')
      if (mode === 'project') {
        return deleteProjectSource(accessToken, sourceId)
      }
      return deleteTenantSource(accessToken, sourceId)
    },
    onSuccess: () => {
      pushToast({ kind: 'success', message: t('toasts.deleted') })
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: () => {
      pushToast({
        kind: 'critical',
        message: t('toasts.delete_failed'),
        duration: 6000,
      })
    },
  })

  async function handleDownload(source: AnySource): Promise<void> {
    if (!accessToken) return
    try {
      const fetcher =
        mode === 'project' ? downloadProjectSource : downloadTenantSource
      const { blob, filename } = await fetcher(accessToken, source.id)
      triggerBrowserDownload(blob, filename ?? source.filename)
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null
      const message = code
        ? t(`errors:${code}` as 'errors:UNKNOWN', {
            defaultValue: t('toasts.download_failed'),
          })
        : t('toasts.download_failed')
      pushToast({ kind: 'critical', message, duration: 6000 })
    }
  }

  function handleDelete(source: AnySource): void {
    const confirmed = window.confirm(
      t('confirm.delete', { filename: source.filename }),
    )
    if (confirmed) {
      deleteMutation.mutate(source.id)
    }
  }

  // ─── Render ────────────────────────────────────────────────────────
  // La denegación manda sobre los datos previos: React Query conserva
  // `data` cuando un refetch falla.
  if (forbiddenTierFallback !== undefined && isForbiddenTier(sourcesQuery.error)) {
    return <>{forbiddenTierFallback}</>
  }

  return (
    <div className="bg-surface border border-line rounded-lg overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-line bg-surface-muted flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-ink-muted">
            {t('header.summary', {
              count: filteredSources.length,
              total: formatSize(totalSize),
            })}
          </p>
        </div>
        {canManage ? (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand hover:bg-brand-deep text-canvas px-3 py-1.5 font-ui text-xs font-medium transition-colors"
          >
            <Upload className="w-3.5 h-3.5" />
            {t('header.upload_button')}
          </button>
        ) : null}
      </div>

      {/* Filtros */}
      <div className="px-6 py-3 border-b border-line flex items-center gap-3 flex-wrap text-xs">
        <label className="flex items-center gap-1.5">
          <span className="text-ink-muted">{t('filters.origin_label')}</span>
          <select
            value={originFilter}
            onChange={(e) =>
              setOriginFilter(e.target.value as SourceOrigin | '')
            }
            className="bg-canvas border border-line rounded-md px-2 py-1 text-xs"
          >
            <option value="">{t('filters.all')}</option>
            {SOURCE_ORIGINS.map((o) => (
              <option key={o} value={o}>
                {t(`origins.${o}` as 'origins.email')}
              </option>
            ))}
          </select>
        </label>
        {mode === 'project' && phaseOptions ? (
          <label className="flex items-center gap-1.5">
            <span className="text-ink-muted">{t('filters.phase_label')}</span>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="bg-canvas border border-line rounded-md px-2 py-1 text-xs"
            >
              <option value="">{t('filters.all')}</option>
              {phaseOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked)
              void queryClient.invalidateQueries({ queryKey })
            }}
            className="rounded"
          />
          <span className="text-ink-muted">
            {t('filters.include_archived')}
          </span>
        </label>
      </div>

      {/* Tabla / empty / loading */}
      {sourcesQuery.isLoading ? (
        <p className="px-6 py-10 text-center font-mono text-xs text-ink-subtle">
          {t('loading')}
        </p>
      ) : filteredSources.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <p className="font-body text-sm text-ink-muted max-w-prose mx-auto">
            {t(mode === 'project' ? 'empty.project' : 'empty.tenant')}
          </p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-ink-muted text-[11px] uppercase tracking-wider border-b border-line">
            <tr>
              <th className="text-left font-medium px-6 py-2.5">
                {t('table.filename')}
              </th>
              <th className="text-left font-medium px-3 py-2.5">
                {t('table.origin')}
              </th>
              {mode === 'project' && phaseOptions ? (
                <th className="text-left font-medium px-3 py-2.5">
                  {t('table.phase')}
                </th>
              ) : null}
              <th className="text-left font-medium px-3 py-2.5">
                {t('table.size')}
              </th>
              <th className="text-left font-medium px-3 py-2.5">
                {t('table.uploaded')}
              </th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filteredSources.map((source) => {
              const phaseLabel =
                'phase_id' in source && source.phase_id && phaseOptions
                  ? phaseOptions.find((p) => p.id === source.phase_id)?.label
                  : null
              return (
                <tr
                  key={source.id}
                  className={
                    source.archived_at
                      ? 'opacity-50 hover:bg-surface-muted'
                      : 'hover:bg-surface-muted'
                  }
                >
                  <td className="px-6 py-3">
                    <div className="font-medium text-ink truncate max-w-280px">
                      {source.filename}
                    </div>
                    {source.description ? (
                      <div className="text-[11px] text-ink-subtle truncate max-w-280px">
                        {source.description}
                      </div>
                    ) : null}
                    {source.tags.length > 0 ? (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {source.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[10px] bg-surface-muted text-ink-muted rounded px-1.5 py-0.5"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-ink-muted text-xs">
                    {t(`origins.${source.origin}` as 'origins.email')}
                  </td>
                  {mode === 'project' && phaseOptions ? (
                    <td className="px-3 py-3 text-ink-muted text-xs">
                      {phaseLabel ?? (
                        <span className="italic text-ink-subtle">
                          {t('table.no_phase')}
                        </span>
                      )}
                    </td>
                  ) : null}
                  <td className="px-3 py-3 text-ink-muted text-xs">
                    {formatSize(source.size_bytes)}
                  </td>
                  <td className="px-3 py-3 text-ink-muted text-xs">
                    {formatDate(source.created_at)}
                    {source.archived_at ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-ink-subtle">
                        · {t('table.archived_badge')}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => void handleDownload(source)}
                      title={t('actions.download')}
                      className="p-1.5 rounded-md text-ink-muted hover:text-ink hover:bg-surface"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {canManage && !source.archived_at ? (
                      <button
                        type="button"
                        onClick={() => archiveMutation.mutate(source.id)}
                        disabled={archiveMutation.isPending}
                        title={t('actions.archive')}
                        className="p-1.5 rounded-md text-ink-muted hover:text-ink hover:bg-surface disabled:opacity-50"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => handleDelete(source)}
                        disabled={deleteMutation.isPending}
                        title={t('actions.delete')}
                        className="p-1.5 rounded-md text-ink-muted hover:text-critical hover:bg-critical-soft disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <UploadSourceDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        {...(mode === 'project' && phaseOptions
          ? { phaseOptions }
          : {})}
        {...(defaultPhaseFilter ? { defaultPhaseId: defaultPhaseFilter } : {})}
        onUpload={async (input) => {
          await uploadMutation.mutateAsync(input)
        }}
      />
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  const locale = toIntlLocale(
    isSupportedLocale(i18n.language) ? i18n.language : 'es',
  )
  try {
    return new Date(iso).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}
